// ---------------------------------------------------------------------------
// Test Mode — simulated financials for Clean Code Culture SRL (CLE-250/253).
//
// Drives the Dashboard financial summary, the Financials overview/trends and
// the dashboard P&L. Target shape (CLE-253):
//   • Revenue   ~42k EUR / month  → ~500k EUR / year
//   • Expenses  ~25k EUR / month  → ~300k EUR / year
//       salaries ~15k + operational (suppliers) ~5k + subcontractors/variable ~5k
//   • Profit    ~17k EUR / month  → ~200k+ EUR / year, POSITIVE every month
// All amounts here are RON.
// ---------------------------------------------------------------------------

import type {
  FinancialSummary,
  DashboardSummary,
  DashboardStats,
  DashboardProjectCard,
  TeamAllocationEntry,
  RecentActivityItem,
  DashboardKpis,
  DashboardToday,
  DashboardTodayInvoice,
  DashboardUrgentTask,
  UpcomingPayments,
  UpcomingPayment,
  TeamAllocationGridEntry,
  ActivityFeedItem,
  DashboardCharts,
  CashflowMonthPoint,
  RevenueByProjectSlice,
  TeamMemberTasks,
} from '../lib/dashboardApi';
import type {
  FinancialOverview,
  FinancialTrends,
  TrendDataPoint,
  MonthlyPnL,
} from '../lib/financialsApi';
import type {
  BankBalances,
  CashflowBucket,
  CashflowHorizon,
  CashflowItem,
  CashflowProjection,
  CurrencyTotal,
  ScheduledPayment,
} from '../lib/cashflowApi';
import { eur, isoDate, isoDateTime } from './company';
import { MONTHLY_SUPPLIER_EXPENSE_RON } from './suppliers';
import { SUBSCRIPTION_SEEDS } from './subscriptions';
import { buildProjectSummaries, allocationsByPerson, projectName } from './projects';
import { buildPeople, PEOPLE_REFS } from './people';
import { buildTasks } from './tasks';

// Salaries ~15k EUR/month (~180k/year). Subcontractors / variable ~5k EUR base.
const SALARIES_RON = eur(15_000);
const VARIABLE_BASE_RON = eur(4_000);

// Deterministic monthly revenue for the trailing 12 months (EUR), averaging
// ~42k and summing to ~500k/year. index 0 = oldest, 11 = current. Every value
// comfortably exceeds the ~25k/month expense so each month's P&L is on the plus
// side (CLE-253). Stored as full EUR amounts so eur() converts them to RON the
// same way the expense constants below are scaled (CLE-266).
const REVENUE_EUR = [
  40_000, 38_000, 42_000, 45_000, 41_000, 44_000, 48_000, 39_000, 43_000, 46_000, 44_000, 40_000,
];

function monthExpense(i: number): number {
  // Fixed (salaries + recurring suppliers ~5k EUR) + a small variable component
  // (subcontractors) so total lands around ~25k EUR/month.
  const variable = VARIABLE_BASE_RON + eur((i % 3) * 500);
  return SALARIES_RON + MONTHLY_SUPPLIER_EXPENSE_RON + variable;
}

function monthLabel(offsetFromCurrent: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetFromCurrent);
  return d.toISOString().slice(0, 7); // YYYY-MM
}

export function buildFinancialSummary(): FinancialSummary {
  const revenue = eur(REVENUE_EUR[11]);
  const expenses = monthExpense(11);
  const profit = revenue - expenses;
  const tvaColectat = Math.round(revenue * 0.19);
  const tvaDeductibil = Math.round(expenses * 0.19);
  return {
    totalRevenue: revenue,
    totalExpenses: expenses,
    profit,
    profitMargin: revenue > 0 ? Math.round((profit / revenue) * 100) : 0,
    tvaColectat,
    tvaDeductibil,
    tvaDePlata: tvaColectat - tvaDeductibil,
    currency: 'RON',
  };
}

function dashboardProjectCards(): DashboardProjectCard[] {
  const tasks = buildTasks();
  return buildProjectSummaries().map((p) => ({
    id: p.id,
    name: p.name,
    status: p.status,
    health: p.health,
    deadline: p.endDate,
    budgetCurrency: p.budgetCurrency,
    budget: p.budget,
    owner: p.owner ? { id: p.owner.id, name: p.owner.name } : null,
    memberCount: p.memberCount,
    openTaskCount: tasks.filter(
      (t) =>
        t.projectId === p.id &&
        t.statusCategory !== 'COMPLETED' &&
        t.statusCategory !== 'CANCELLED',
    ).length,
  }));
}

function teamAllocation(): TeamAllocationEntry[] {
  const people = buildPeople();
  const allocByPerson = allocationsByPerson();
  return people.map((person) => {
    const allocs = allocByPerson[person.id] ?? [];
    const totalHours = allocs.reduce((sum, a) => sum + a.hours, 0);
    const totalPercent = person.capacity > 0 ? Math.round((totalHours / person.capacity) * 100) : 0;
    return {
      person: {
        id: person.id,
        name: person.fullName,
        title: person.title,
        capacity: person.capacity,
      },
      projects: allocs.map((a) => ({
        projectId: a.projectId,
        projectName: projectName(a.projectId),
        percentage: person.capacity > 0 ? Math.round((a.hours / person.capacity) * 100) : 0,
      })),
      totalPercent,
      utilizationPercent: person.utilizationPercent,
      overAllocated: person.isOverallocated,
    };
  });
}

export function buildDashboardSummary(): DashboardSummary {
  return {
    financials: buildFinancialSummary(),
    projects: dashboardProjectCards(),
    teamAllocation: teamAllocation(),
  };
}

export function buildDashboardStats(): DashboardStats {
  const projects = buildProjectSummaries();
  const tasks = buildTasks();
  return {
    totalProjects: projects.length,
    activeProjects: projects.filter((p) => p.status === 'ACTIVE' || p.status === 'ALMOST_DONE')
      .length,
    atRiskProjects: projects.filter((p) => p.health !== 'ON_TRACK').length,
    totalPeople: PEOPLE_REFS.length,
    openInvoices: 8,
    openTasks: tasks.filter(
      (t) => t.statusCategory !== 'COMPLETED' && t.statusCategory !== 'CANCELLED',
    ).length,
    overdueInvoices: 2,
    invoicesTracked: true,
  };
}

export function buildRecentActivity(limit = 10): RecentActivityItem[] {
  const projects = buildProjectSummaries();
  const items: RecentActivityItem[] = [];
  projects.slice(0, 4).forEach((p, i) => {
    items.push({
      id: `act-proj-${p.id}`,
      type: 'project',
      action: i % 2 === 0 ? 'updated' : 'created',
      title: p.name,
      description: `Proiectul ${p.name} a fost actualizat.`,
      actor: p.owner?.name ?? null,
      timestamp: isoDateTime(-(i + 1), 11),
    });
  });
  buildTasks()
    .slice(0, 4)
    .forEach((t, i) => {
      items.push({
        id: `act-task-${t.id}`,
        type: 'task',
        action: 'updated',
        title: t.title,
        description: `Sarcina ${t.identifier} a fost mutată în ${t.statusLabel}.`,
        actor: t.assignee?.name ?? null,
        timestamp: isoDateTime(-(i + 1), 13),
        task: urgentTask(t),
      });
    });
  return items.slice(0, limit);
}

// --- Financials module -----------------------------------------------------

export function buildFinancialOverview(): FinancialOverview {
  const curRevenue = eur(REVENUE_EUR[11]);
  const curExpenses = monthExpense(11);
  const prevRevenue = eur(REVENUE_EUR[10]);
  const prevExpenses = monthExpense(10);
  const curProfit = curRevenue - curExpenses;
  const prevProfit = prevRevenue - prevExpenses;
  const pct = (cur: number, prev: number) =>
    prev !== 0 ? Math.round(((cur - prev) / Math.abs(prev)) * 100) : 0;
  return {
    current: {
      period: monthLabel(0),
      totalRevenue: curRevenue,
      totalExpenses: curExpenses,
      profit: curProfit,
      profitMargin: Math.round((curProfit / curRevenue) * 100),
      currency: 'RON',
    },
    previous: {
      period: monthLabel(-1),
      totalRevenue: prevRevenue,
      totalExpenses: prevExpenses,
      profit: prevProfit,
      profitMargin: Math.round((prevProfit / prevRevenue) * 100),
      currency: 'RON',
    },
    change: {
      revenueChange: pct(curRevenue, prevRevenue),
      expensesChange: pct(curExpenses, prevExpenses),
      profitChange: pct(curProfit, prevProfit),
      marginChange: 0,
    },
  };
}

export function buildFinancialTrends(): FinancialTrends {
  const dataPoints: TrendDataPoint[] = REVENUE_EUR.map((r, i) => {
    const revenue = eur(r);
    const expenses = monthExpense(i);
    return {
      period: monthLabel(i - 11),
      revenue,
      expenses,
      profit: revenue - expenses,
    };
  });
  // Two months of naive forecast continuing the trend.
  const forecast: TrendDataPoint[] = [1, 2].map((n) => {
    const revenue = eur(46_000);
    const expenses = monthExpense((11 + n) % 12);
    return { period: monthLabel(n), revenue, expenses, profit: revenue - expenses };
  });
  return { range: '1y', granularity: 'month', dataPoints, forecast };
}

// ---------------------------------------------------------------------------
// COO Executive Command Center (CLE-256) — Test Mode builders.
//
// These mirror the backend dashboardService shapes and feed the new command
// center sections. Amounts stay coherent with the P&L above (~40k EUR / ~200k
// RON revenue this month, positive profit) and synthesize near-term due dates
// the static invoice/task seeds don't carry.
// ---------------------------------------------------------------------------

export function buildDashboardKpis(): DashboardKpis {
  const revenue = eur(REVENUE_EUR[11]);
  const expenses = monthExpense(11);
  const profit = revenue - expenses;
  const prevRevenue = eur(REVENUE_EUR[10]);
  const prevExpenses = monthExpense(10);
  const pct = (cur: number, prev: number) =>
    prev !== 0 ? Math.round(((cur - prev) / Math.abs(prev)) * 100) : 0;

  const tasks = buildTasks();
  const overdue = tasks.filter(
    (t) =>
      t.statusCategory !== 'COMPLETED' &&
      t.statusCategory !== 'CANCELLED' &&
      t.dueDate !== null &&
      new Date(t.dueDate).getTime() < Date.now(),
  ).length;
  const critical = tasks.filter(
    (t) =>
      (t.statusCategory === 'STARTED' || t.statusCategory === 'UNSTARTED') &&
      (t.priority === 'URGENT' || t.priority === 'HIGH'),
  ).length;

  return {
    revenueThisMonth: revenue,
    expensesThisMonth: expenses,
    profitThisMonth: profit,
    profitMargin: revenue > 0 ? Math.round((profit / revenue) * 100) : 0,
    revenueTrendPct: pct(revenue, prevRevenue),
    expensesTrendPct: pct(expenses, prevExpenses),
    // Two outstanding client invoices, three supplier payments queued.
    unpaidReceivables: eur(28_000),
    unpaidReceivablesCount: 2,
    payablesDue: eur(11_500),
    payablesDueCount: 3,
    urgentTaskCount: overdue + critical,
    overdueTaskCount: overdue,
    criticalTaskCount: critical,
    currency: 'RON',
  };
}

function todayInvoice(
  id: string,
  invoiceNumber: string,
  party: string,
  amountEur: number,
  dueOffset: number,
  status: string,
): DashboardTodayInvoice {
  return {
    id,
    invoiceNumber,
    party,
    amount: eur(amountEur),
    dueDate: isoDate(dueOffset),
    status,
    currency: 'RON',
  };
}

function urgentTask(task: ReturnType<typeof buildTasks>[number]): DashboardUrgentTask {
  const due = task.dueDate ? new Date(task.dueDate).getTime() : null;
  const daysOverdue =
    due !== null && due < Date.now() ? Math.floor((Date.now() - due) / (1000 * 60 * 60 * 24)) : 0;
  return {
    id: task.id,
    identifier: task.identifier,
    title: task.title,
    projectName: task.projectName,
    assignee: task.assignee?.name ?? null,
    priority: task.priority,
    status: task.status,
    statusLabel: task.statusLabel,
    statusColor: task.statusColor,
    statusCategory: task.statusCategory,
    dueDate: task.dueDate,
    daysOverdue,
  };
}

export function buildDashboardToday(): DashboardToday {
  const tasks = buildTasks();
  const overdueTasks = tasks
    .filter(
      (t) =>
        t.statusCategory !== 'COMPLETED' &&
        t.statusCategory !== 'CANCELLED' &&
        t.dueDate !== null &&
        new Date(t.dueDate).getTime() < Date.now(),
    )
    .slice(0, 5)
    .map(urgentTask);
  const criticalTasks = tasks
    .filter(
      (t) =>
        (t.statusCategory === 'STARTED' || t.statusCategory === 'UNSTARTED') &&
        (t.priority === 'URGENT' || t.priority === 'HIGH'),
    )
    .slice(0, 5)
    .map(urgentTask);
  const blockedTasks = tasks
    .filter((t) => t.status === 'in_review' && (t.priority === 'URGENT' || t.priority === 'HIGH'))
    .slice(0, 3)
    .map(urgentTask);

  return {
    incomingToday: [
      todayInvoice('inv-today-in-1', 'TF-2041', 'Retail Dynamics SRL', 8_500, 0, 'SENT'),
      todayInvoice('inv-today-in-2', 'TF-2042', 'MedTech Solutions', 6_200, 0, 'OVERDUE'),
    ],
    outgoingToday: [
      todayInvoice('inv-today-out-1', '5521-AWS', 'Amazon Web Services', 2_400, 0, 'SENT'),
    ],
    invoicesIssuedToday: [
      todayInvoice('inv-issued-1', 'TF-2043', 'FinScale GmbH', 12_000, 25, 'SENT'),
    ],
    invoicesReceivedToday: [
      todayInvoice('inv-received-1', '7782-OFC', 'Regus Office Cluj', 1_800, 12, 'SENT'),
    ],
    overdueTasks,
    criticalTasks,
    blockedTasks,
  };
}

export function buildUpcomingPayments(days = 14): UpcomingPayments {
  // Synthetic schedule for the next ~2 weeks. Income green, outcome red.
  const seeds: Array<{
    type: 'income' | 'outcome';
    number: string;
    party: string;
    amountEur: number;
    dueOffset: number;
    status: string;
  }> = [
    {
      type: 'income',
      number: 'TF-2042',
      party: 'MedTech Solutions',
      amountEur: 6_200,
      dueOffset: 0,
      status: 'OVERDUE',
    },
    {
      type: 'outcome',
      number: '5521-AWS',
      party: 'Amazon Web Services',
      amountEur: 2_400,
      dueOffset: 1,
      status: 'SENT',
    },
    {
      type: 'income',
      number: 'TF-2044',
      party: 'Retail Dynamics SRL',
      amountEur: 9_800,
      dueOffset: 3,
      status: 'SENT',
    },
    {
      type: 'outcome',
      number: '4410-SAL',
      party: 'Salarii echipă',
      amountEur: 7_500,
      dueOffset: 5,
      status: 'SENT',
    },
    {
      type: 'income',
      number: 'TF-2045',
      party: 'FinScale GmbH',
      amountEur: 12_000,
      dueOffset: 8,
      status: 'SENT',
    },
    {
      type: 'outcome',
      number: '7782-OFC',
      party: 'Regus Office Cluj',
      amountEur: 1_800,
      dueOffset: 10,
      status: 'SENT',
    },
    {
      type: 'income',
      number: 'TF-2046',
      party: 'Nordic Apps AB',
      amountEur: 7_400,
      dueOffset: 12,
      status: 'SENT',
    },
  ];
  const items: UpcomingPayment[] = seeds
    .filter((s) => s.dueOffset <= days)
    .map((s, i) => ({
      id: `up-${i}`,
      type: s.type,
      invoiceNumber: s.number,
      party: s.party,
      amount: eur(s.amountEur),
      dueDate: isoDate(s.dueOffset),
      status: s.status,
      currency: 'RON',
    }));
  const totalIncome = items
    .filter((i) => i.type === 'income')
    .reduce((sum, i) => sum + i.amount, 0);
  const totalOutcome = items
    .filter((i) => i.type === 'outcome')
    .reduce((sum, i) => sum + i.amount, 0);
  return {
    days,
    items,
    totalIncome,
    totalOutcome,
    netTotal: totalIncome - totalOutcome,
    currency: 'RON',
  };
}

export function buildTeamAllocationGrid(): TeamAllocationGridEntry[] {
  const people = buildPeople();
  const allocByPerson = allocationsByPerson();
  const tasks = buildTasks();
  return people
    .filter((p) => (allocByPerson[p.id] ?? []).length > 0)
    .map((person) => {
      const allocs = allocByPerson[person.id] ?? [];
      const inProgress = tasks.filter(
        (t) => t.assigneeId === person.id && t.status === 'in_progress',
      ).length;
      const hasOverdueTasks = tasks.some(
        (t) =>
          t.assigneeId === person.id &&
          t.statusCategory !== 'COMPLETED' &&
          t.statusCategory !== 'CANCELLED' &&
          t.dueDate !== null &&
          new Date(t.dueDate).getTime() < Date.now(),
      );
      const util = person.utilizationPercent;
      const status: TeamAllocationGridEntry['status'] =
        util > 100 ? 'overloaded' : util >= 60 ? 'busy' : 'free';
      return {
        person: { id: person.id, name: person.fullName, title: person.title },
        projects: allocs.map((a) => ({
          projectId: a.projectId,
          projectName: projectName(a.projectId),
        })),
        inProgressTasks: inProgress,
        utilizationPercent: util,
        status,
        hasOverdueTasks,
      };
    });
}

export function buildActivityFeed(limit = 15): ActivityFeedItem[] {
  return buildRecentActivity(limit).map((item) => {
    let link: string | null = null;
    if (item.type === 'project') link = `/projects/${item.id.replace('act-proj-', '')}`;
    else if (item.type === 'task') link = `/tasks/${item.id.replace('act-task-', '')}`;
    else if (item.type === 'invoice') link = '/invoices';
    return { ...item, link };
  });
}

// ---------------------------------------------------------------------------
// Charts + per-person team tasks (CLE-257) — Test Mode builders.
// ---------------------------------------------------------------------------

const RO_MONTHS_SHORT = [
  'ian',
  'feb',
  'mar',
  'apr',
  'mai',
  'iun',
  'iul',
  'aug',
  'sep',
  'oct',
  'noi',
  'dec',
];

export function buildDashboardCharts(): DashboardCharts {
  // Revenue distribution across active projects: split the current month's
  // revenue proportionally so the donut stays coherent with the P&L.
  const projects = buildProjectSummaries().filter(
    (p) => p.status === 'ACTIVE' || p.status === 'ALMOST_DONE',
  );
  const totalRevenue = eur(REVENUE_EUR[11]);
  // Deterministic weights (sum normalized) per project, weighted toward the
  // first few so the donut has a clear leader.
  const weights = projects.map((_, i) => 1 / (i + 1.5));
  const weightSum = weights.reduce((s, w) => s + w, 0) || 1;
  const revenueByProject: RevenueByProjectSlice[] = projects
    .map((p, i) => {
      const amount = Math.round((totalRevenue * weights[i]) / weightSum);
      return {
        projectId: p.id,
        projectName: p.name,
        amount,
        percent: totalRevenue > 0 ? Math.round((amount / totalRevenue) * 100) : 0,
      };
    })
    .filter((s) => s.amount > 0)
    .sort((a, b) => b.amount - a.amount);

  // Trailing six months of income vs expenses (oldest → current).
  const cashflow6m: CashflowMonthPoint[] = [5, 4, 3, 2, 1, 0].map((back) => {
    const idx = 11 - back; // index into the 12-month REVENUE_EUR array
    const income = eur(REVENUE_EUR[idx]);
    const expenses = monthExpense(idx);
    const d = new Date();
    d.setMonth(d.getMonth() - back);
    return {
      period: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: RO_MONTHS_SHORT[d.getMonth()],
      income,
      expenses,
      profit: income - expenses,
    };
  });

  return {
    revenueByProject,
    cashflow6m,
    revenueSparkline: cashflow6m.map((m) => m.income),
    expensesSparkline: cashflow6m.map((m) => m.expenses),
    profitSparkline: cashflow6m.map((m) => m.profit),
    currency: 'RON',
  };
}

export function buildTeamTasks(): TeamMemberTasks[] {
  const people = buildPeople();
  const allocByPerson = allocationsByPerson();
  const tasks = buildTasks();

  return people
    .map((person) => {
      const open = tasks
        .filter(
          (t) =>
            t.assigneeId === person.id &&
            t.statusCategory !== 'COMPLETED' &&
            t.statusCategory !== 'CANCELLED',
        )
        .map(urgentTask);
      const allocatedHours = (allocByPerson[person.id] ?? []).reduce((sum, a) => sum + a.hours, 0);
      const utilizationPercent =
        person.capacity > 0 ? Math.round((allocatedHours / person.capacity) * 100) : 0;
      return {
        person: { id: person.id, name: person.fullName, title: person.title },
        tasks: open,
        inProgressCount: open.filter((t) => t.status === 'in_progress').length,
        overdueCount: open.filter((t) => t.daysOverdue > 0).length,
        utilizationPercent,
      };
    })
    .filter((entry) => entry.tasks.length > 0)
    .sort((a, b) => b.tasks.length - a.tasks.length);
}

// ---------------------------------------------------------------------------
// Cash flow (Financiar › Plăți & Încasări viitoare) — Test Mode builders.
// Mirrors backend modules/financials/financials.cashflow.ts: monthly
// subscriptions on paymentDay, PLANNED scheduled payments, unpaid invoices
// (one overdue in the current bucket), strictly per-currency totals.
// ---------------------------------------------------------------------------

/** ISO date (UTC midnight) for a calendar day `monthOffset` months from now,
 *  with the day clamped to the target month's length. */
function cashflowDate(monthOffset: number, day: number): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + monthOffset;
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  return new Date(Date.UTC(y, m, Math.min(day, daysInMonth))).toISOString();
}

function cashflowMonthKey(monthOffset: number): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function buildScheduledPayments(): ScheduledPayment[] {
  const seeds: Array<{
    id: string;
    direction: ScheduledPayment['direction'];
    name: string;
    amount: number;
    monthOffset: number;
    day: number;
  }> = [
    {
      id: 'sp1',
      direction: 'PAYMENT',
      name: 'Chirie birou Cluj',
      amount: 5_000,
      monthOffset: 1,
      day: 5,
    },
    {
      id: 'sp2',
      direction: 'RECEIPT',
      name: 'Avans proiect Retail Dynamics',
      amount: 12_000,
      monthOffset: 1,
      day: 15,
    },
    {
      id: 'sp3',
      direction: 'PAYMENT',
      name: 'Asigurare echipamente',
      amount: 1_800,
      monthOffset: 2,
      day: 10,
    },
  ];
  return seeds.map((s) => ({
    id: s.id,
    direction: s.direction,
    name: s.name,
    amount: s.amount,
    currency: 'RON',
    dueDate: cashflowDate(s.monthOffset, s.day),
    notes: null,
    status: 'PLANNED' as const,
    companyId: null,
    company: null,
    supplierId: null,
    supplier: null,
    clientId: null,
    client: null,
    projectId: null,
    project: null,
    expenseCategoryId: null,
    expenseCategory: null,
    createdAt: isoDateTime(-10, 9),
    updatedAt: isoDateTime(-2, 14),
  }));
}

// Mirrors buildBankAccounts() in testData/settings.ts (ba-1/ba-2/ba-3):
// ba-2 has a manual override; the RON accounts come "from statements".
export function buildCashflowBalances(): BankBalances {
  const ronMainAsOf = isoDateTime(-4, 9);
  const ronIngAsOf = isoDateTime(-12, 9);
  const eurManualAt = isoDateTime(-8, 12);
  return {
    accounts: [
      {
        id: 'ba-1',
        bankName: 'Banca Transilvania',
        label: 'Cont principal RON',
        iban: 'RO49BTRL0123456789012345',
        currency: 'RON',
        balance: 84_230.5,
        asOf: ronMainAsOf,
        source: 'statement',
        manualBalance: null,
        manualBalanceAt: null,
      },
      {
        id: 'ba-2',
        bankName: 'Banca Transilvania',
        label: 'Cont EUR',
        iban: 'RO49BTRL0123456789012346',
        currency: 'EUR',
        balance: 12_400,
        asOf: eurManualAt,
        source: 'manual',
        manualBalance: 12_400,
        manualBalanceAt: eurManualAt,
      },
      {
        id: 'ba-3',
        bankName: 'ING Bank',
        label: 'Cont ING RON',
        iban: 'RO49INGB0001000100010001',
        currency: 'RON',
        balance: 15_760.25,
        asOf: ronIngAsOf,
        source: 'statement',
        manualBalance: null,
        manualBalanceAt: null,
      },
    ],
    totals: [
      // asOf = oldest contributing timestamp per currency.
      { currency: 'EUR', balance: 12_400, asOf: eurManualAt },
      { currency: 'RON', balance: 99_990.75, asOf: ronIngAsOf },
    ],
  };
}

export function buildCashflowProjection(months: CashflowHorizon): CashflowProjection {
  const today = new Date().getDate();
  const buckets: CashflowBucket[] = [];
  const balances = buildCashflowBalances();
  // Running balance per currency; currencies without a known balance stay null.
  const running = new Map<string, number>();
  for (const t of balances.totals) running.set(t.currency, t.balance);

  for (let i = 0; i < months; i++) {
    const items: CashflowItem[] = [];
    const key = cashflowMonthKey(i);

    // Monthly subscriptions (USD) on their paymentDay; in the current month,
    // occurrences before today have already happened.
    SUBSCRIPTION_SEEDS.slice(0, 6).forEach((s, idx) => {
      if (s.expectedAmount === null) return;
      const day = ((idx * 3) % 28) + 1;
      if (i === 0 && day < today) return;
      items.push({
        id: `${s.id}:${key}`,
        source: 'subscription',
        direction: 'out',
        name: s.name,
        date: cashflowDate(i, day),
        amount: s.expectedAmount,
        currency: 'USD',
        refId: s.id,
      });
    });

    // Planned payments/receipts (RON) whose due month lands in this bucket.
    for (const sp of buildScheduledPayments()) {
      if (sp.dueDate.slice(0, 7) !== key) continue;
      items.push({
        id: sp.id,
        source: 'planned',
        direction: sp.direction === 'RECEIPT' ? 'in' : 'out',
        name: sp.name,
        date: sp.dueDate,
        amount: sp.amount,
        currency: sp.currency,
        refId: sp.id,
      });
    }

    // Unpaid invoices: one overdue receivable in the current bucket, one
    // future receivable next month.
    if (i === 0) {
      items.push({
        id: 'inv-cf-overdue',
        source: 'invoice',
        direction: 'in',
        name: 'TF-2042 — MedTech Solutions',
        date: isoDate(-6),
        amount: eur(6_200),
        currency: 'RON',
        isOverdue: true,
        refId: 'inv-cf-overdue',
      });
    }
    if (i === 1) {
      items.push({
        id: 'inv-cf-next',
        source: 'invoice',
        direction: 'in',
        name: 'TF-2045 — FinScale GmbH',
        date: cashflowDate(1, 20),
        amount: eur(12_000),
        currency: 'RON',
        refId: 'inv-cf-next',
      });
    }

    items.sort((a, b) => a.date.localeCompare(b.date));
    const byCurrency = currencyTotals(items);
    for (const c of byCurrency) {
      const current = running.get(c.currency);
      if (current !== undefined) {
        running.set(c.currency, Math.round((current + c.net) * 100) / 100);
      }
    }
    const currencies = new Set<string>([...running.keys(), ...byCurrency.map((c) => c.currency)]);
    const projectedBalance = [...currencies]
      .sort()
      .map((currency) => ({ currency, balance: running.get(currency) ?? null }));
    buckets.push({ month: key, byCurrency, items, projectedBalance });
  }

  return {
    months,
    startMonth: cashflowMonthKey(0),
    buckets,
    totalsByCurrency: currencyTotals(buckets.flatMap((b) => b.items)),
    excluded: { quarterlyAnnualSubscriptions: 1, usageSubscriptions: 0 },
    balances,
  };
}

function currencyTotals(items: CashflowItem[]): CurrencyTotal[] {
  const map = new Map<string, CurrencyTotal>();
  for (const item of items) {
    let entry = map.get(item.currency);
    if (!entry) {
      entry = { currency: item.currency, inflow: 0, outflow: 0, net: 0 };
      map.set(item.currency, entry);
    }
    if (item.direction === 'in') entry.inflow += item.amount;
    else entry.outflow += item.amount;
    entry.net = entry.inflow - entry.outflow;
  }
  return [...map.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

export function buildMonthlyPnL(year: number, month: number): MonthlyPnL {
  const revenue = eur(REVENUE_EUR[11]);
  const expenses = monthExpense(11);
  const profit = revenue - expenses;
  return {
    period: `${year}-${String(month).padStart(2, '0')}`,
    year,
    month,
    totalRevenue: revenue,
    totalExpenses: expenses,
    profit,
    profitMargin: Math.round((profit / revenue) * 100),
    currency: 'RON',
    revenueByCategory: [
      { category: 'Servicii dezvoltare', amount: Math.round(revenue * 0.8) },
      { category: 'Mentenanță', amount: Math.round(revenue * 0.2) },
    ],
    expensesByCategory: [
      { category: 'Salarii', amount: SALARIES_RON },
      { category: 'Furnizori recurenți', amount: MONTHLY_SUPPLIER_EXPENSE_RON },
      {
        category: 'Costuri variabile',
        amount: expenses - SALARIES_RON - MONTHLY_SUPPLIER_EXPENSE_RON,
      },
    ],
  };
}
